// src/components/DriverDetails.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiFolder } from "react-icons/fi";
import axios from "../axiosConfig";
import "../styles/DriverDetails.css";

/* mesmos países usados no CreateDriver (para extrair bandeira/prefixo) */
const countryOptions = [
  { label: "Austria", value: "43", code: "at" },
  { label: "Belgium", value: "32", code: "be" },
  { label: "Croatia", value: "385", code: "hr" },
  { label: "Czech Republic", value: "420", code: "cz" },
  { label: "Denmark", value: "45", code: "dk" },
  { label: "Estonia", value: "372", code: "ee" },
  { label: "Finland", value: "358", code: "fi" },
  { label: "France", value: "33", code: "fr" },
  { label: "Germany", value: "49", code: "de" },
  { label: "Hungary", value: "36", code: "hu" },
  { label: "Italy", value: "39", code: "it" },
  { label: "Netherlands", value: "31", code: "nl" },
  { label: "Norway", value: "47", code: "no" },
  { label: "Poland", value: "48", code: "pl" },
  { label: "Portugal", value: "351", code: "pt" },
  { label: "Spain", value: "34", code: "es" },
  { label: "Sweden", value: "46", code: "se" },
  { label: "Switzerland", value: "41", code: "ch" },
  { label: "United Kingdom", value: "44", code: "gb" },
];

function getCountryByPhoneNumber(phone) {
  if (!phone || !phone.startsWith("+")) return null;
  const digits = phone.replace(/[^\d]/g, "");
  const match = [...countryOptions]
    .sort((a, b) => b.value.length - a.value.length)
    .find((opt) => digits.startsWith(opt.value));
  return match || null;
}

function formatDate(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-GB");
}

/* ===== URL builder sem localhost hardcoded + normalização de barras ===== */
const API_BASE = (axios?.defaults?.baseURL || "").replace(/\/+$/, "");
const getFileUrl = (p) => {
  if (!p) return "";
  if (typeof p !== "string") {
    try {
      return URL.createObjectURL(p);
    } catch {
      return "";
    }
  }
  if (/^https?:\/\//i.test(p)) return p; // já é absoluto
  const clean = ("/" + p).replace(/\\/g, "/").replace(/\/{2,}/g, "/");
  return `${API_BASE}${clean}`;
};

/* ==== Adapter: normaliza driveroriginal/legacy para as chaves usadas na UI ==== */
function adaptDriver(d) {
  if (!d) return null;
  return {
    // Básicos
    fullname: d.fullname ?? d.name ?? "",
    email: d.email ?? "",
    phoneNumber: d.phoneNumber ?? d.contact_number ?? "",
    birth_date: d.birth_date ?? d.date_of_birth ?? "",

    // Identificação
    tax_id: d.tax_id ?? d.id_number ?? "",
    tax_id_expiry: d.tax_id_expiry ?? d.DriverIDExpiry ?? "",
    driver_license_number: d.driving_license_number ?? d.driver_license ?? "",
    driver_license_expiry: d.driver_license_expiry ?? d.DriverLicenseExpiry ?? "",
    license_country: d.license_country ?? "",
    license_categories: d.license_categories ?? d.licenseCategories ?? "",

    tacho_card_number: d.tacho_card_number ?? d.driver_tacho_number ?? "",

    // Documentos
    profile_photo: d.profile_photo ?? null,
    driver_id_photo: d.driver_id_photo ?? null,
    driver_license_photo: d.driver_license_photo ?? null,
    tacho_card_photo: d.tacho_card_photo ?? d.driver_tacho_card_photo ?? null,

    // Certificações / skills
    adr_hazmat_certification:
      d.adr_hazmat_certification ?? d.adr_hazmar_certification ?? false,
    crane_hiab_license: d.crane_hiab_license ?? false,
    tanker_experience: d.tanker_experience ?? false,
    refrigerated_experience: d.refrigerated_experience ?? false,
    night_weekend_driving: d.night_weekend_driving ?? false,
    urgent_jobs: d.urgent_jobs ?? false,
    languages_spoken: d.languages_spoken ?? "",
    lanes_preference: d.lanes_preference ?? "",
    sleep_in_truck: d.sleep_in_truck ?? false,

    // raw p/ compatibilidade no sendToEdit
    __raw: d,
  };
}

function DriverDetails() {
  const { driverId } = useParams();
  const navigate = useNavigate();

  const [driver, setDriver] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchDriverDetails = async () => {
      try {
        let data;
        try {
          // Novo modelo (driveroriginal)
          const r1 = await axios.get(`/driveroriginal/${driverId}`);
          data = r1.data;
        } catch (e) {
          // Fallback compat: legacy /drivers/:id
          const r2 = await axios.get(`/drivers/${driverId}`);
          data = r2.data;
        }
        setDriver(data);
        setErrorMessage("");
      } catch (err) {
        console.error("[DriverDetails] fetch error:", err);
        setErrorMessage("Failed to load driver details. Please try again later.");
      }
    };
    fetchDriverDetails();
  }, [driverId]);

  if (!driver) {
    return (
      <div className="dd-container">
        <div className="dd-loading">{errorMessage || "Loading driver details..."}</div>
      </div>
    );
  }

  const d = adaptDriver(driver);

  const c = getCountryByPhoneNumber(d.phoneNumber);
  const numberNoPrefix =
    c && d.phoneNumber ? d.phoneNumber.replace("+" + c.value, "") : d.phoneNumber || "";

  const sendToEdit = () => {
    navigate(`/drivers/${driverId}/edit`, {
      state: {
        prefill: {
          // NOVO MODELO
          fullname: d.fullname,
          email: d.email,
          phoneNumber: d.phoneNumber,
          birth_date: d.birth_date,

          tax_id: d.tax_id,
          tax_id_expiry: d.tax_id_expiry,
          driving_license_number: d.driver_license_number,
          driver_license_expiry: d.driver_license_expiry,
          license_country: d.license_country,
          license_categories: d.license_categories,

          tacho_card_number: d.tacho_card_number,

          profile_photo: d.profile_photo,
          driver_id_photo: d.driver_id_photo,
          driver_license_photo: d.driver_license_photo,
          tacho_card_photo: d.tacho_card_photo,

          adr_hazmat_certification: d.adr_hazmat_certification,
          crane_hiab_license: d.crane_hiab_license,
          tanker_experience: d.tanker_experience,
          refrigerated_experience: d.refrigerated_experience,
          night_weekend_driving: d.night_weekend_driving,
          urgent_jobs: d.urgent_jobs,
          languages_spoken: d.languages_spoken,
          lanes_preference: d.lanes_preference,
          sleep_in_truck: d.sleep_in_truck,

          // LEGACY (fallback para o editor atual não partir)
          name: d.fullname || driver.name || "",
          contact_number: d.phoneNumber || driver.contact_number || "",
          date_of_birth: d.birth_date || driver.date_of_birth || "",
          driver_id: d.tax_id || driver.driver_id || "",
          DriverIDExpiry: d.tax_id_expiry || driver.DriverIDExpiry || "",
          driver_license: d.driver_license_number || driver.driver_license || "",
          DriverLicenseExpiry:
            d.driver_license_expiry || driver.DriverLicenseExpiry || "",
          driver_tacho_number: d.tacho_card_number || driver.driver_tacho_number || "",
          id_number: d.tax_id || driver.id_number || "",
          driver_tacho_card_photo:
            d.tacho_card_photo || driver.driver_tacho_card_photo || null,
        },
      },
    });
  };

  return (
    <div className="dd-container">
      {/* Back */}
      <div className="dd-back-row">
        <button className="dd-back" onClick={() => navigate("/drivers")}>
          &larr; Back
        </button>
      </div>

      <div className="dd-content">
        <h1 className="dd-title">DRIVER DETAILS</h1>
        {errorMessage && <p className="dd-msg-error">{errorMessage}</p>}

        <div className="dd-card">
          {/* ===== Basic Information ===== */}
          <h2 className="dd-section-title">Basic Information</h2>
          <div className="dd-basic">
            {/* Foto à esquerda */}
            <div className="dd-profile">
              <div className="dd-profile-circle">
                {d.profile_photo ? (
                  <img
                    className="dd-profile-img"
                    src={getFileUrl(d.profile_photo)}
                    alt="Profile"
                  />
                ) : (
                  <span className="dd-profile-ph">No Photo</span>
                )}
              </div>
            </div>

            {/* Campos à direita */}
            <div className="dd-basic-fields">
              <div className="dd-field">
                <label className="dd-label">Full Name</label>
                <div className="dd-value">{d.fullname || "N/A"}</div>
              </div>

              <div className="dd-field">
                <label className="dd-label">Email</label>
                <div className="dd-value">{d.email || "N/A"}</div>
              </div>

              <div className="dd-field">
                <label className="dd-label">Contact Phone</label>
                <div className="dd-value dd-phone">
                  {c ? (
                    <>
                      <img
                        className="dd-flag"
                        src={`https://flagcdn.com/w40/${c.code}.png`}
                        alt={c.label}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                      <span className="dd-prefix">+{c.value}</span>
                      <span className="dd-number">{numberNoPrefix}</span>
                    </>
                  ) : (
                    <span className="dd-number">{d.phoneNumber || "N/A"}</span>
                  )}
                </div>
              </div>

              <div className="dd-field">
                <label className="dd-label">Date of Birth</label>
                <div className="dd-value">{formatDate(d.birth_date)}</div>
              </div>
            </div>
          </div>

          {/* ===== Identification ===== */}
          <h2 className="dd-section-title">Identification</h2>

          <div className="dd-row-2">
            <div className="dd-field">
              <label className="dd-label">Tax ID</label>
              <div className="dd-value">{d.tax_id || "N/A"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Tax ID Expiry</label>
              <div className="dd-value">{formatDate(d.tax_id_expiry)}</div>
            </div>
          </div>

          <div className="dd-row-2">
            <div className="dd-field">
              <label className="dd-label">Driver License Number</label>
              <div className="dd-value">{d.driver_license_number || "N/A"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Driver License Expiry</label>
              <div className="dd-value">{formatDate(d.driver_license_expiry)}</div>
            </div>
          </div>

          <div className="dd-row-2">
            <div className="dd-field">
              <label className="dd-label">License Country</label>
              <div className="dd-value">{d.license_country || "N/A"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">License Categories</label>
              <div className="dd-value">
                {Array.isArray(d.license_categories)
                  ? d.license_categories.join(", ")
                  : d.license_categories || "N/A"}
              </div>
            </div>
          </div>

          <div className="dd-row-2">
            <div className="dd-field">
              <label className="dd-label">Tacho Card Number</label>
              <div className="dd-value">{d.tacho_card_number || "N/A"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Sleeps in Truck</label>
              <div className="dd-value">{d.sleep_in_truck ? "Yes" : "No"}</div>
            </div>
          </div>

          {/* ===== Certifications & Skills ===== */}
          <h2 className="dd-section-title">Certifications & Skills</h2>
          <div className="dd-row-3">
            <div className="dd-field">
              <label className="dd-label">ADR / HazMat</label>
              <div className="dd-value">{d.adr_hazmat_certification ? "Yes" : "No"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Crane/HIAB License</label>
              <div className="dd-value">{d.crane_hiab_license ? "Yes" : "No"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Tanker Experience</label>
              <div className="dd-value">{d.tanker_experience ? "Yes" : "No"}</div>
            </div>
          </div>

          <div className="dd-row-3">
            <div className="dd-field">
              <label className="dd-label">Refrigerated Experience</label>
              <div className="dd-value">{d.refrigerated_experience ? "Yes" : "No"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Night/Weekend Driving</label>
              <div className="dd-value">{d.night_weekend_driving ? "Yes" : "No"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Urgent Jobs</label>
              <div className="dd-value">{d.urgent_jobs ? "Yes" : "No"}</div>
            </div>
          </div>

          <div className="dd-row-2">
            <div className="dd-field">
              <label className="dd-label">Languages</label>
              <div className="dd-value">{d.languages_spoken || "N/A"}</div>
            </div>
            <div className="dd-field">
              <label className="dd-label">Lanes Preference</label>
              <div className="dd-value">{d.lanes_preference || "N/A"}</div>
            </div>
          </div>

          {/* ===== Uploaded Documents ===== */}
          <h2 className="dd-section-title">Uploaded Documents</h2>
          <div className="dd-row-3">
            <div className="dd-field">
              <label className="dd-label">National ID / Citizen Card</label>
              {d.driver_id_photo ? (
                <a
                  className="dd-file-link"
                  href={getFileUrl(d.driver_id_photo)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiFolder />
                  <span>View file</span>
                </a>
              ) : (
                <div className="dd-file-missing">No file uploaded</div>
              )}
            </div>

            <div className="dd-field">
              <label className="dd-label">Driver License Photo</label>
              {d.driver_license_photo ? (
                <a
                  className="dd-file-link"
                  href={getFileUrl(d.driver_license_photo)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiFolder />
                  <span>View file</span>
                </a>
              ) : (
                <div className="dd-file-missing">No file uploaded</div>
              )}
            </div>

            <div className="dd-field">
              <label className="dd-label">Tacho Card Photo</label>
              {d.tacho_card_photo ? (
                <a
                  className="dd-file-link"
                  href={getFileUrl(d.tacho_card_photo)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiFolder />
                  <span>View file</span>
                </a>
              ) : (
                <div className="dd-file-missing">No file uploaded</div>
              )}
            </div>
          </div>

          {/* Botões */}
          <div className="dd-buttons">
            <button className="dd-btn-cancel" type="button" onClick={() => navigate("/drivers")}>
              Back
            </button>
            <button className="dd-btn-submit" type="button" onClick={sendToEdit}>
              Edit Driver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverDetails;
